// Detect Swagger UI by presence of its DOM elements rather than URL matching.
// This allows the enhancements (filter, collapse, download) to work on any page
// that renders a Swagger UI, regardless of the URL path.
// Add exclusions if needed
const excludedPaths = [
    // Add any paths you want to exclude
    // '/admin/openapi-specification',
];

function shouldRunOnCurrentPage() {
    const currentPath = window.location.pathname.toLowerCase();

    // Check exclusions
    const isExcluded = excludedPaths.some(excluded =>
        currentPath.includes(excluded)
    );
    if (isExcluded) {
        return false;
    }

    // Detect Swagger UI by the presence of its characteristic wrapper element
    return !!document.querySelector('.swagger-ui');
}

/**
 * Some Swagger UI fields (e.g. summaries, enum values) are rendered as plain
 * text and bypass the Remarkable Markdown component, so literal **text** is
 * never converted to <strong>. This function installs a MutationObserver on the
 * Swagger UI root that converts any remaining **bold** text nodes to <strong>,
 * covering both the initial render and all subsequent lazy expansions (Swagger
 * UI v5 adds operation details to the DOM only when the user expands a section).
 */
function installMarkdownBoldObserver() {
    const container = document.querySelector('.swagger-ui');
    if (!container) return;

    function processBoldInNode(root) {
        const walker = document.createTreeWalker(
            root,
            NodeFilter.SHOW_TEXT,
            {
                acceptNode(node) {
                    const parent = node.parentElement;
                    if (!parent) return NodeFilter.FILTER_REJECT;
                    const tag = parent.tagName.toUpperCase();
                    if (['SCRIPT', 'STYLE', 'CODE', 'PRE', 'TEXTAREA', 'INPUT', 'STRONG', 'B'].includes(tag)) {
                        return NodeFilter.FILTER_REJECT;
                    }
                    return node.nodeValue && node.nodeValue.includes('**')
                        ? NodeFilter.FILTER_ACCEPT
                        : NodeFilter.FILTER_REJECT;
                }
            }
        );

        const nodes = [];
        let node;
        while ((node = walker.nextNode())) nodes.push(node);

        nodes.forEach(textNode => {
            const text = textNode.nodeValue;
            if (!text.includes('**')) return;
            const span = document.createElement('span');
            span.innerHTML = text.replace(/\*\*(.+?)\*\*/g, '<strong>$1</strong>');
            textNode.parentNode.replaceChild(span, textNode);
        });
    }

    // Process whatever is already in the DOM
    processBoldInNode(container);

    // Process every node added from this point on (lazy renders, expansions)
    const observer = new MutationObserver(mutations => {
        mutations.forEach(mutation => {
            mutation.addedNodes.forEach(added => {
                if (added.nodeType === Node.ELEMENT_NODE) {
                    processBoldInNode(added);
                }
            });
        });
    });

    observer.observe(container, { childList: true, subtree: true });
}

function setupSwaggerEnhancements(specUrl) {
    const tagSections = document.querySelectorAll('.opblock-tag-section');
    if (tagSections.length === 0) {
        return false;
    }

    // --- Collapse all endpoints on load ---
    tagSections.forEach(section => {
        if (section.classList.contains('is-open')) {
            const tagHeader = section.querySelector('.opblock-tag');
            if (tagHeader) {
                tagHeader.click();
            }
        }
    });

    // --- Install MutationObserver to render **bold** markdown in all descriptions
    //     (covers both initial render and lazy expansions triggered by user clicks) ---
    installMarkdownBoldObserver();

    // --- Find the main wrapper for endpoints ---
    let mainWrapper = null;
    document.querySelectorAll('.wrapper').forEach(wrapper => {
        if (wrapper.querySelector('.opblock-tag-section')) {
            mainWrapper = wrapper;
        }
    });

    if (!mainWrapper) {
        return false;
    }

    // Check if filter already exists to avoid duplicates
    if (mainWrapper.querySelector('.swagger-filter-container')) {
        return true; // Already initialized
    }

    setupFilterUI(mainWrapper);
    setupDownloadButton(mainWrapper, specUrl);
    return true;
}

function setupFilterUI(mainWrapper) {
    // --- Create the filter box and logic directly here ---
    const filterContainer = document.createElement('span');
    filterContainer.className = 'swagger-filter-container';

    const filterBox = document.createElement('input');
    filterBox.type = 'text';
    filterBox.placeholder = 'Filter endpoints by URL or Part of URL';

    const clearBtn = document.createElement('span');
    clearBtn.innerHTML = '&#215;';
    clearBtn.title = 'Clear';
    clearBtn.className = 'swagger-clear-btn';

    function openAllGroups() {
        document.querySelectorAll('.opblock-tag-section').forEach(section => {
            const tagHeader = section.querySelector('.opblock-tag');
            if (tagHeader && !section.classList.contains('is-open')) {
                tagHeader.click();
            }
        });
    }

    function collapseAllGroups() {
        setTimeout(() => {
            document.querySelectorAll('.opblock-tag-section').forEach(section => {
                const tagHeader = section.querySelector('.opblock-tag');
                if (tagHeader && section.classList.contains('is-open')) {
                    tagHeader.click();
                }
                section.style.display = '';
            });
        }, 0);
    }

    function doFilter() {
        const filter = filterBox.value.toLowerCase();
        clearBtn.style.display = filter ? 'block' : 'none';
        openAllGroups();
        
        document.querySelectorAll('.opblock-tag-section').forEach(section => {
            let anyVisible = false;
            section.querySelectorAll('.opblock').forEach(opblock => {
                const path = opblock.querySelector('.opblock-summary-path');
                if (path && path.textContent.toLowerCase().includes(filter)) {
                    opblock.style.display = '';
                    anyVisible = true;
                } else {
                    opblock.style.display = 'none';
                }
            });
            const tagHeader = section.querySelector('.opblock-tag');
            if (filter) {
                if (anyVisible) {
                    section.style.display = '';
                    if (!section.classList.contains('is-open')) {
                        tagHeader && tagHeader.click();
                    }
                } else {
                    section.style.display = 'none';
                    if (section.classList.contains('is-open')) {
                        tagHeader && tagHeader.click();
                    }
                }
            } else {
                section.style.display = '';
                section.querySelectorAll('.opblock').forEach(opblock => {
                    opblock.style.display = '';
                });
            }
        });
        if (!filter) {
            collapseAllGroups();
        }
    }

    // Debounce function to limit filter execution
    function debounce(func, delay) {
        let timeoutId;
        return function (...args) {
            clearTimeout(timeoutId);
            timeoutId = setTimeout(() => func.apply(this, args), delay);
        };
    }

    const debouncedFilter = debounce(doFilter, 0);

    filterBox.oninput = debouncedFilter;
    clearBtn.onclick = () => {
        filterBox.value = '';
        doFilter();
        filterBox.focus();
    };
    filterContainer.appendChild(filterBox);
    filterContainer.appendChild(clearBtn);

    // Insert filterContainer at the top of the mainWrapper
    if (mainWrapper) {
        mainWrapper.insertBefore(filterContainer, mainWrapper.firstChild);
    }
}

function setupDownloadButton(mainWrapper, specUrl) {
    // Require a spec URL — passed explicitly from the Vue component so we do
    // not depend on the (now-hidden) raw OAS file link in the Info section.
    if (!specUrl) return;

    // Create download button container
    const downloadContainer = document.createElement('div');
    downloadContainer.className = 'swagger-download-container';
    downloadContainer.style.cssText = 'display: inline-block; margin: 10px;';

    // Create download button
    const downloadBtn = document.createElement('button');
    downloadBtn.className = 'btn swagger-download-btn';
    downloadBtn.textContent = '📥 Download OpenAPI Spec';
    downloadBtn.style.cssText = `
        padding: 8px 16px;
        background-color: #4990e2;
        color: white;
        border: none;
        border-radius: 4px;
        cursor: pointer;
        font-size: 14px;
        font-weight: 500;
    `;
    
    // Hover effect
    downloadBtn.onmouseenter = () => {
        downloadBtn.style.backgroundColor = '#357abd';
    };
    downloadBtn.onmouseleave = () => {
        downloadBtn.style.backgroundColor = '#4990e2';
    };

    // Download handler
    downloadBtn.onclick = async () => {
        try {
            // Use the URL passed from the Vue component rather than scraping
            // the DOM — the raw OAS file link is no longer shown on the page.
            if (!specUrl) {
                alert('Unable to determine OpenAPI specification URL');
                return;
            }

            // Show loading state
            const originalText = downloadBtn.textContent;
            downloadBtn.textContent = '⏳ Downloading...';
            downloadBtn.disabled = true;

            // Fetch the spec
            const response = await fetch(specUrl);
            const specContent = await response.text();
            
            // Determine file format (JSON or YAML)
            let filename = 'openapi-spec.yaml';
            let mimeType = 'application/x-yaml';
            
            try {
                JSON.parse(specContent);
                filename = 'openapi-spec.json';
                mimeType = 'application/json';
            } catch {
                // It's YAML, keep defaults
            }

            // Create blob and download
            const blob = new Blob([specContent], { type: mimeType });
            const downloadUrl = window.URL.createObjectURL(blob);
            const a = document.createElement('a');
            a.href = downloadUrl;
            a.download = filename;
            document.body.appendChild(a);
            a.click();
            document.body.removeChild(a);
            window.URL.revokeObjectURL(downloadUrl);

            // Reset button state
            downloadBtn.textContent = originalText;
            downloadBtn.disabled = false;
        } catch (error) {
            console.error('Download failed:', error);
            alert('Failed to download OpenAPI specification');
            downloadBtn.textContent = '📥 Download OpenAPI Spec';
            downloadBtn.disabled = false;
        }
    };

    downloadContainer.appendChild(downloadBtn);

    // Insert next to the filter box
    const filterContainer = mainWrapper.querySelector('.swagger-filter-container');
    if (filterContainer && filterContainer.parentNode) {
        filterContainer.parentNode.insertBefore(downloadContainer, filterContainer.nextSibling);
    } else {
        mainWrapper.insertBefore(downloadContainer, mainWrapper.firstChild);
    }
}

// Main function to initialize enhancements.
// specUrl – original URL from which the OAS spec was loaded; forwarded to
//           the download button so the raw (un-enriched) file is saved.
export function initializeSwaggerUIEnhancements(specUrl = null) {
    // Early exit if Swagger UI is not present on this page
    if (!shouldRunOnCurrentPage()) {
        return;
    }

    // Since we're called from onComplete, elements should be ready
    setupSwaggerEnhancements(specUrl);
}

// Default export
export default initializeSwaggerUIEnhancements;